import { useState, useRef, useEffect, type KeyboardEvent } from "react";
import type { Skill } from "@cowork/shared";
import { cn } from "../../lib/utils.js";

interface Props {
  onSend: (text: string, skill?: Skill) => void;
  skills?: readonly Skill[];
  disabled?: boolean;
  onCancel?: () => void;
  streaming?: boolean;
}

export function ChatInput({ onSend, skills = [], disabled, onCancel, streaming }: Props) {
  const [value, setValue] = useState("");
  const [activeSkill, setActiveSkill] = useState<Skill | null>(null);
  const [suggestions, setSuggestions] = useState<readonly Skill[]>([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!value.startsWith("/") || activeSkill) {
      setSuggestions([]);
      return;
    }
    const query = value.slice(1).toLowerCase();
    const filtered = skills.filter(
      (s) => s.name.toLowerCase().includes(query) || s.description.toLowerCase().includes(query),
    );
    setSuggestions(filtered);
    setSuggestionIdx(0);
  }, [value, skills, activeSkill]);

  const applySkill = (skill: Skill) => {
    setActiveSkill(skill);
    setSuggestions([]);
    setValue("");
    textareaRef.current?.focus();
  };

  const clearSkill = () => {
    setActiveSkill(null);
    textareaRef.current?.focus();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (suggestions.length > 0) {
      if (e.key === "ArrowDown") { e.preventDefault(); setSuggestionIdx((i) => Math.min(i + 1, suggestions.length - 1)); return; }
      if (e.key === "ArrowUp")   { e.preventDefault(); setSuggestionIdx((i) => Math.max(i - 1, 0)); return; }
      if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); const s = suggestions[suggestionIdx]; if (s) applySkill(s); return; }
      if (e.key === "Escape") { setSuggestions([]); return; }
    }
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); }
  };

  const submit = () => {
    const text = value.trim();
    if ((!text && !activeSkill) || disabled) return;
    const messageText = text || `Apply the ${activeSkill?.name ?? ""} skill to this conversation.`;
    onSend(messageText, activeSkill ?? undefined);
    setValue("");
    setActiveSkill(null);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleInput = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  };

  const hasContent = value.trim() || activeSkill;

  return (
    <div className="px-4 pb-4 pt-2">
      {/* Skill suggestions */}
      {suggestions.length > 0 && (
        <div
          className="mb-2 rounded-xl overflow-hidden shadow-lg"
          style={{ border: "1px solid var(--border)", background: "var(--bg-secondary)" }}
        >
          {suggestions.map((s, i) => (
            <button
              key={s.name}
              onMouseDown={(e) => { e.preventDefault(); applySkill(s); }}
              className="w-full text-left px-4 py-2.5 flex items-baseline gap-3 transition-colors"
              style={{
                background: i === suggestionIdx ? "var(--accent-muted)" : "transparent",
              }}
            >
              <span className="text-xs font-mono font-medium" style={{ color: "var(--accent)" }}>/{s.name}</span>
              <span className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{s.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Input container */}
      <div
        className="rounded-2xl transition-colors"
        style={{
          background: "var(--bg-secondary)",
          border: "1px solid var(--border)",
        }}
      >
        {/* Active skill badge */}
        {activeSkill && (
          <div className="flex items-center px-4 pt-3">
            <button
              onClick={clearSkill}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors"
              style={{
                background: "var(--accent-muted)",
                border: "1px solid var(--accent-border)",
                color: "var(--accent)",
              }}
            >
              <span>/{activeSkill.name}</span>
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="1" y1="1" x2="7" y2="7" /><line x1="7" y1="1" x2="1" y2="7" />
              </svg>
            </button>
          </div>
        )}

        <div className="flex items-end gap-3 px-4 py-3">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            disabled={disabled && !streaming}
            placeholder={activeSkill ? `Add context for /${activeSkill.name}…` : "How can I help you?"}
            rows={1}
            className={cn(
              "flex-1 resize-none bg-transparent outline-none text-sm leading-relaxed",
              "min-h-[24px] max-h-[200px]",
            )}
            style={{
              color: "var(--text-primary)",
            }}
          />

          {streaming ? (
            <button
              onClick={onCancel}
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-colors mb-0.5"
              style={{ background: "rgba(248,113,113,0.15)", color: "#f87171" }}
              title="Stop generating"
            >
              <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
                <rect x="1.5" y="1.5" width="7" height="7" rx="1.5" />
              </svg>
            </button>
          ) : (
            <button
              onClick={submit}
              disabled={!hasContent || !!disabled}
              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all mb-0.5"
              style={{
                background: hasContent && !disabled ? "var(--accent)" : "var(--bg-tertiary)",
                color: hasContent && !disabled ? "white" : "var(--text-tertiary)",
              }}
              title="Send (Enter)"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="7" y1="12" x2="7" y2="2" />
                <polyline points="3,6 7,2 11,6" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <p className="text-[10px] mt-1.5 px-1 text-center" style={{ color: "var(--text-tertiary)" }}>
        Type <kbd className="font-mono">/</kbd> to use a skill · Enter to send · Shift+Enter for new line
      </p>
    </div>
  );
}
